import * as THREE from 'three';
import {worldFetch} from './world-transport';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone } from 'three/addons/utils/SkeletonUtils.js';
import type { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { createKTX2Loader } from './ktx2';
import { z } from 'zod';
import { assetVersionSchema, clipSchema, definitionSchema, BENTO_HASH, sha256, interactionBundleSchema, type InteractionBundle, type AssetVersion, type Clip, type Rig } from './asset-contract';

export interface Template { version: AssetVersion; scene: THREE.Group | null; clip: Clip | null; interaction?: InteractionBundle; textures?: THREE.Texture[] }
export interface AssetHandle { template: Template; release(): void }
interface Entry { id: string; refs: number; cost: number; used: number; orphan: boolean; abort: AbortController; promise: Promise<Template>; value?: Template }
const MAX_CACHE = 160 << 20;

async function boundedBytes(response: Response, expected: number): Promise<Uint8Array> {
  if (!response.ok || !response.body) throw new Error(`Asset download failed (${response.status})`);
  const reader = response.body.getReader(), chunks: Uint8Array[] = []; let size = 0;
  try { for (;;) { const { value, done } = await reader.read(); if (done) break; size += value.byteLength; if (size > expected) throw new Error('Asset bytes exceed manifest'); chunks.push(value); } }
  catch (error) { await reader.cancel(); throw error; }
  if (size !== expected) throw new Error('Asset download incomplete');
  const bytes = new Uint8Array(size); let offset = 0; for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; } return bytes;
}
function disposeTemplate(template: Template): void {
  if (!template.scene) return;
  const geometries = new Set<THREE.BufferGeometry>(), materials = new Set<THREE.Material>(), textures = new Set<THREE.Texture>(template.textures), skeletons = new Set<THREE.Skeleton>();
  template.scene.traverse(node => {
    if (node instanceof THREE.Mesh) { geometries.add(node.geometry); for (const m of Array.isArray(node.material) ? node.material : [node.material]) { materials.add(m); for (const value of Object.values(m)) if (value instanceof THREE.Texture) textures.add(value); } }
    if (node instanceof THREE.SkinnedMesh) skeletons.add(node.skeleton);
  });
  geometries.forEach(g => g.dispose()); materials.forEach(m => m.dispose()); skeletons.forEach(s => s.dispose());
  textures.forEach(t => { const image = t.image as { close?: () => void } | undefined; image?.close?.(); t.dispose(); });
}
export function instantiate(template: Template): { root: THREE.Group; dispose(): void } {
  if (!template.scene) throw new Error('Asset is not a model');
  const root = clone(template.scene) as THREE.Group; const materials = new Set<THREE.Material>(), skeletons = new Set<THREE.Skeleton>();
  root.traverse(node => {
    if (node instanceof THREE.Mesh) {
      node.material = Array.isArray(node.material) ? node.material.map(m => m.clone()) : node.material.clone();
      for (const m of Array.isArray(node.material) ? node.material : [node.material]) materials.add(m);
      node.castShadow = true; node.receiveShadow = true;
    }
    if (node instanceof THREE.SkinnedMesh) { skeletons.add(node.skeleton); node.frustumCulled = false; }
  });
  let disposed = false;
  return { root, dispose() { if (disposed) return; disposed = true; root.removeFromParent(); materials.forEach(m => m.dispose()); skeletons.forEach(s => s.dispose()); } };
}

export class AssetCache {
  private entries = new Map<string, Entry>();
  private generation = 0;
  private cost = 0;
  private active = 0;
  private waiting: (() => void)[] = [];
  private rigPromise: Promise<Rig> | null = null;
  private ktx2: KTX2Loader | undefined;
  private closing = false;
  private anisotropy = 1;
  configure(renderer: THREE.WebGLRenderer): void { this.ktx2 ??= createKTX2Loader(renderer); }
  get busy(): boolean { return this.active>0 || this.waiting.length>0; }
  setAnisotropy(value: number): void {
    this.anisotropy = value;
    for (const entry of this.entries.values()) for (const texture of entry.value?.textures ?? []) this.filterTexture(texture);
  }
  private filterTexture(texture: THREE.Texture): void {
    if (texture.anisotropy !== this.anisotropy) { texture.anisotropy = this.anisotropy; texture.needsUpdate = true; }
  }
  rig(): Promise<Rig> {
    if (!this.rigPromise) this.rigPromise = (async () => {
      const response = await worldFetch('/api/rigs/bento-v1'); if (!response.ok) throw new Error('Rig unavailable');
      const value = z.strictObject({ definition: definitionSchema, hash: z.literal(BENTO_HASH) }).parse(await response.json());
      if (await sha256(new TextEncoder().encode(JSON.stringify(value.definition))) !== value.hash) throw new Error('Rig definition hash mismatch');
      return value.definition;
    })().catch(error => { this.rigPromise = null; throw error; });
    return this.rigPromise;
  }
  async acquire(id: string): Promise<AssetHandle> {
    if (this.closing) throw new Error('Asset cache disposed');
    let entry = this.entries.get(id);
    if (!entry) {
      this.evict(0, true);
      if (this.entries.size >= 16) throw new Error('Client asset slot budget exhausted');
      entry = { id, refs: 0, cost: 0, used: performance.now(), orphan: false, abort: new AbortController(), promise: Promise.resolve(null as unknown as Template) };
      this.entries.set(id, entry); entry.promise = this.load(entry, this.generation);
    }
    entry.refs++; const owned = entry;
    try {
      const template = await entry.promise; if (owned.orphan) throw new Error('Asset session changed');
      let released = false;
      return { template, release: () => { if (released) return; released = true; owned.refs--; owned.used = performance.now(); if (owned.orphan && owned.refs === 0) this.dispose(owned); } };
    } catch (error) { owned.refs--; if (this.entries.get(id) === owned) this.entries.delete(id); if (owned.refs === 0) this.dispose(owned); throw error; }
  }
  private async load(entry: Entry, generation: number): Promise<Template> {
    if (this.active >= 2) await new Promise<void>(resolve => this.waiting.push(resolve));
    else this.active++;
    try {
      if (entry.orphan) throw new Error('Asset session changed');
      const response = await worldFetch(`/api/assets/versions/${encodeURIComponent(entry.id)}`, { signal: entry.abort.signal });
      if (!response.ok) throw new Error(`Asset unavailable (${response.status})`);
      const version = assetVersionSchema.parse(await response.json());
      if (version.id !== entry.id) throw new Error('Asset identity mismatch');
      // Reserve RGBA8 fallback plus a full mip pyramid. Two base levels also
      // cover highly rectangular images, whose mip overhead can approach 100%.
      const cost = version.bytes + version.report.texturePixels * 8;
      this.evict(cost); if (this.cost + cost > MAX_CACHE) throw new Error('Client decoded asset budget exhausted');
      entry.cost = cost; this.cost += cost;
      const bytes = await boundedBytes(await worldFetch(`/api/assets/versions/${entry.id}/blob`, { signal: entry.abort.signal }), version.bytes);
      if (await sha256(bytes) !== version.hash) throw new Error('Asset content hash mismatch');
      let template: Template;
      if (version.kind === 'animation') template = { version, scene: null, clip: clipSchema.parse(JSON.parse(new TextDecoder().decode(bytes)) as unknown) };
      else if (version.kind === 'interaction') {
        const interaction = interactionBundleSchema.parse(JSON.parse(new TextDecoder().decode(bytes)) as unknown);
        if (JSON.stringify(interaction.spec) !== JSON.stringify(version.report.interaction)) throw new Error('Interaction manifest mismatch');
        template = { version, scene: null, clip: null, interaction };
      }
      else {
        const manager = new THREE.LoadingManager(); manager.setURLModifier(url => { if (!url.startsWith('blob:')) throw new Error('External asset URI refused'); return url; });
        const loader = new GLTFLoader(manager); if (this.ktx2) loader.setKTX2Loader(this.ktx2);
        const gltf = await loader.parseAsync(bytes.buffer as ArrayBuffer, '');
        const textures = await gltf.parser.getDependencies('texture') as (THREE.Texture | null)[];
        template = { version, scene: gltf.scene, clip: null, textures: textures.filter((texture): texture is THREE.Texture => texture !== null) };
        // GLTFLoader otherwise swallows texture errors and returns an untextured mesh.
        if (textures.some(texture => texture === null)) { disposeTemplate(template); throw new Error('Asset texture could not be decoded or transcoded'); }
        template.textures?.forEach(texture => this.filterTexture(texture));
      }
      if (generation !== this.generation || entry.orphan) { disposeTemplate(template); throw new Error('Asset session changed'); }
      entry.value = template; return template;
    } finally {
      const next = this.waiting.shift(); if (next) next(); else this.active--;
      if (this.closing && this.active === 0) this.disposeTranscoder();
    }
  }
  private evict(required: number, slot = false): void {
    const candidates = [...this.entries.values()].filter(e => e.refs === 0 && e.value).sort((a, b) => a.used - b.used);
    while ((this.cost + required > MAX_CACHE || (slot && this.entries.size >= 16)) && candidates.length) { const e = candidates.shift()!; this.entries.delete(e.id); this.dispose(e); }
  }
  private dispose(entry: Entry): void { if (entry.value) { disposeTemplate(entry.value); entry.value = undefined; } this.cost -= entry.cost; entry.cost = 0; }
  reset(): void {
    this.generation++;
    for (const entry of this.entries.values()) { entry.orphan = true; entry.abort.abort(); if (entry.refs === 0) this.dispose(entry); }
    this.entries.clear();
  }
  private disposeTranscoder(): void {
    // Three.js tracks initialized loaders; disposing a never-used one decrements its counter.
    if (this.ktx2?.transcoderPending) this.ktx2.dispose(); this.ktx2 = undefined;
  }
  shutdown(): void { this.closing = true; this.reset(); if (this.active === 0) this.disposeTranscoder(); }
  get diagnostics() {
    return { entries: this.entries.size, bytes: this.cost, loading: this.active, waiting: this.waiting.length,
      textures: [...this.entries.values()].flatMap(entry => (entry.value?.textures ?? []).map(texture => ({
        assetId: entry.id, mime: texture.userData.mimeType as string | undefined, format: texture.format,
        width: (texture.image as { width?: number } | undefined)?.width, height: (texture.image as { height?: number } | undefined)?.height,
        mipmaps: texture.mipmaps.length, colorSpace: texture.colorSpace, anisotropy: texture.anisotropy,
      }))),
    };
  }
}
